/**
 * ArenaPanel — on-demand PvP-opponent spawn control for the multi-level Arena.
 *
 * Reuses the grounds scenario-bot plumbing: the server (LobbyToolsHandler) gates
 * these `spawn_scenario_bot` tools to GROUNDS_TOOLS_MAP_IDS, which now includes
 * `arena-multi-level`. Spawns dummy / standing-caster / aggressive opponents near
 * you (or clears the ones you spawned) so DJ can stage specific PvP matchups on
 * top of the auto-spawned roster. Auto-opens on the Arena surface.
 */

import React, { useState } from 'react';
import { ServiceRegistry } from '../../core/ServiceRegistry';
import { gameLogger } from '../../../utils/GameLogger';
import {
  GROUNDS_SCENARIO_MAX_COUNT,
  LOBBY_TOOL_DEFINITIONS,
  LOBBY_TOOL_MESSAGE,
  LOBBY_TOOLS,
  LobbyToolId,
} from '../../../shared/constants/LobbyTools';

/** Scenario-bot tools, in panel order (same set the Grounds uses). */
const SCENARIO_SPAWN_TOOLS = LOBBY_TOOL_DEFINITIONS.filter(
  (d) => d.kind === 'spawn_scenario_bot' && d.groundsOnly,
);

function sendScenarioTool(tool: LobbyToolId, count?: number): boolean {
  const nm = ServiceRegistry.getInstance().get('NetworkManager');
  const room = nm?.getRoom?.();
  if (!room) {
    gameLogger.warn('arena', 'No Colyseus room available for scenario-bot send');
    return false;
  }
  room.send(LOBBY_TOOL_MESSAGE, count != null ? { tool, count } : { tool });
  return true;
}

export const ArenaPanel: React.FC = () => {
  const [count, setCount] = useState<number>(1);
  const spawn = (tool: LobbyToolId) => sendScenarioTool(tool, count);
  const clear = () => sendScenarioTool(LOBBY_TOOLS.DESPAWN_SCENARIO_BOTS);

  return (
    <div style={panel}>
      <div style={header}>🏟️ Arena — <span style={{ color: '#e67e22' }}>spawn opponents</span></div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
        <span style={dim}>count</span>
        <select
          value={count}
          onChange={(e) => setCount(Number(e.target.value))}
          style={select}
          data-testid="arena-scenario-count"
        >
          {Array.from({ length: GROUNDS_SCENARIO_MAX_COUNT }, (_, i) => i + 1).map((n) => (
            <option key={n} value={n}>{n}</option>
          ))}
        </select>
        <span style={dim}>{count === 1 ? '1v1' : `1v${count}`}</span>
      </div>
      <div style={chipRow}>
        {SCENARIO_SPAWN_TOOLS.map((t) => (
          <button
            key={t.id}
            style={scenarioBtn}
            title={t.description}
            data-testid={`arena-${t.id}`}
            onClick={() => spawn(t.id)}
          >
            {t.icon} {t.label}
          </button>
        ))}
        <button
          style={clearBtn}
          title="Despawn all opponents you spawned."
          data-testid="arena-despawn-scenario-bots"
          onClick={clear}
        >
          🧹 Clear
        </button>
      </div>
      <div style={footer}>Aggressive opponents chase + fight (PvP free-for-all). The auto-spawned roster fights too.</div>
    </div>
  );
};

// ---- styles (consistent with the dark diagnostic-panel family) -------------
const panel: React.CSSProperties = {
  position: 'fixed', left: 16, bottom: 90, width: 300, zIndex: 9000,
  background: 'rgba(16,18,26,0.95)', border: '1px solid #3a4256', borderRadius: 8,
  color: '#e6e6e6', fontFamily: 'monospace', fontSize: 12, padding: 10,
  boxShadow: '0 6px 24px rgba(0,0,0,0.5)',
};
const header: React.CSSProperties = { fontWeight: 'bold', fontSize: 13, marginBottom: 8, borderBottom: '1px solid #2a3142', paddingBottom: 6 };
const select: React.CSSProperties = { flex: '0 0 56px', background: '#0e1018', color: '#e6e6e6', border: '1px solid #3a4256', borderRadius: 4, padding: '4px 6px', fontFamily: 'monospace', fontSize: 12 };
const chipRow: React.CSSProperties = { display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 6 };
const scenarioBtn: React.CSSProperties = { background: '#243044', color: '#dfe8f5', border: '1px solid #36507a', borderRadius: 4, padding: '4px 8px', cursor: 'pointer', fontSize: 11, fontWeight: 600 };
const clearBtn: React.CSSProperties = { background: '#3a2430', color: '#f5d8df', border: '1px solid #7a3650', borderRadius: 4, padding: '4px 8px', cursor: 'pointer', fontSize: 11, fontWeight: 600 };
const dim: React.CSSProperties = { color: '#778', fontStyle: 'italic' };
const footer: React.CSSProperties = { color: '#667', fontSize: 10, borderTop: '1px solid #2a3142', paddingTop: 6, marginTop: 6 };
